// PostHog (WTK-728): feature flags, so a new feature can be shown to chosen people first, and page
// views. EU Cloud. Off unless VUE_APP_POSTHOG_KEY is set for the build.
//
// Privacy: persistence "memory" sets no cookie and stores nothing in the browser; autocapture,
// session recording and surveys are off. The logged-in user is identified by their WhereToKnow id
// with their e-mail (what flags target people by); the property being viewed goes to flag
// evaluation only.
import Vue from "vue";
import posthog from "posthog-js";
import { isStaging } from "./env";
import { isDeclaredFlag } from "@/config/featureFlags";

const KEY = (process.env.VUE_APP_POSTHOG_KEY || "").trim();
const HOST = (process.env.VUE_APP_POSTHOG_HOST || "https://eu.i.posthog.com").trim();

let started = false;
// Reactive, so a template reading isFeatureOn() updates once the flags arrive.
const state = Vue.observable({ enabled: {} });
// flagsSettled(): set by identifyUser; gaveUp once PostHog did not answer in time, so a slow or
// blocked PostHog delays one load only.
let identified = false;
let gaveUp = false;
const FLAGS_WAIT_MS = 800;

export function startAnalytics(router) {
  if (!KEY || started) return;
  posthog.init(KEY, {
    api_host: HOST,
    persistence: "memory",
    person_profiles: "identified_only",
    autocapture: false,
    capture_pageview: false,
    capture_pageleave: false,
    disable_session_recording: true,
    // No in-app surveys: their script is not loaded at all.
    disable_surveys: true,
  });
  started = true;
  posthog.register({ environment: environment() });
  posthog.setPersonPropertiesForFlags({ environment: environment() }, false);
  posthog.onFeatureFlags((flags) => {
    const enabled = {};
    (flags || []).forEach((f) => {
      enabled[f] = true;
    });
    state.enabled = enabled;
  });
  // A single-page app: one page view per route.
  router.afterEach((to) => {
    posthog.capture("$pageview", { $current_url: window.location.origin + to.path, route: to.name || to.path });
  });
}

function environment() {
  return isStaging() ? "staging" : "production";
}

// After login (/auth/me): who is using the dashboard.
export function identifyUser(user) {
  if (!started || !user || !user.id) return;
  posthog.identify(String(user.id), { email: user.email, username: user.username });
  identified = true;
}

// posthog-js 1.435 internals: a flags request running or queued. Fields it no longer has read as
// "not loading", which only makes flagsSettled() stop waiting earlier.
function flagsLoading() {
  const ff = posthog.featureFlags;
  return !!(ff && (ff._requestInFlight || ff._reloadDebouncer || ff._additionalReloadRequested));
}

// Resolves once the flags of the signed-in user are in, for code that decides once (when it
// loads data) instead of re-rendering when the flags arrive. identifyUser makes PostHog load them
// again, and the answer for the anonymous visitor can still arrive first, so the first flags are
// not enough. Waits FLAGS_WAIT_MS at most; at once with PostHog off.
export function flagsSettled() {
  if (!started) return Promise.resolve();
  const until = Date.now() + FLAGS_WAIT_MS;
  return new Promise((resolve) => {
    const check = () => {
      if (gaveUp || (identified && !flagsLoading())) return resolve();
      if (Date.now() >= until) {
        gaveUp = true;
        return resolve();
      }
      setTimeout(check, 50);
    };
    check();
  });
}

// The property being viewed ({ entityType, entityId }, utils/libredeskEntity.js): flags can be
// switched on per hotel / restaurant / brand. Used for flag evaluation only.
export function setCurrentUnit(entity) {
  if (!started) return;
  const props = entity && entity.entityType && entity.entityType !== "NONE"
    ? { unit_type: entity.entityType, unit_id: String(entity.entityId) }
    : { unit_type: "NONE", unit_id: "" };
  posthog.setPersonPropertiesForFlags(props);
}

export function resetAnalytics() {
  if (!started) return;
  posthog.reset();
  state.enabled = {};
  identified = false;
  gaveUp = false;
  posthog.setPersonPropertiesForFlags({ environment: environment() }, false);
}

export function flagOn(key) {
  if (process.env.NODE_ENV !== "production" && !isDeclaredFlag(key)) {
    // eslint-disable-next-line no-console
    console.warn(`Feature flag "${key}" is not declared in src/config/featureFlags.js (format wtk-<ticket>-<what>)`);
  }
  return !!state.enabled[key];
}
